import { useCallback } from "react";
import { FirestoreProductSubject, SubjectSource } from "reedy-data/models";
import { v4 } from "uuid";
import { useProduct } from "~/contexts/product-context";
import { logError } from "~/shared/services/error-reporting";
import { BaseItem } from "~/components/sortable-list";
import {
  filterSubjects,
  filterQualifiers,
} from "~/pages/product-details/tabs/thema-tab/utils";
import { useProductActions } from "~/hooks/use-product-actions";
import {
  getKeywordsByType,
  SubjectSchemeIdentifier,
} from "~/integrations/firebase/collections";

const subjectMatches = (
  a: FirestoreProductSubject,
  b: FirestoreProductSubject,
): boolean =>
  a.subjectCode !== null ? a.subjectCode === b.subjectCode : a.id === b.id;

export const useSubjects = () => {
  const { product } = useProduct();
  const { saveFinalSubjects, saveFinalKeywords, saveFinalWordSubjects } =
    useProductActions();

  const handleToggleSubject = useCallback(
    async (subject: FirestoreProductSubject) => {
      if (!product) {
        logError("Product not found while toggling subject");
        return;
      }
      if (!subject) {
        return;
      }

      const finalSubjects = product.subjects?.final || [];
      const existingIndex = finalSubjects.findIndex((existing) =>
        subjectMatches(subject, existing),
      );
      const isAdding = existingIndex === -1;

      const updatedSubjects = isAdding
        ? [...finalSubjects, subject]
        : finalSubjects.filter((_, index) => index !== existingIndex);

      const isKeyword =
        subject.subjectSchemeIdentifier === SubjectSchemeIdentifier.Keyword;

      if (isKeyword) {
        await saveFinalKeywords(product.id, updatedSubjects);
      } else {
        await saveFinalSubjects(product.id, updatedSubjects);
      }
    },
    [product, saveFinalSubjects, saveFinalKeywords],
  );

  const handleAddSubjects = useCallback(
    async (subjects: FirestoreProductSubject[]) => {
      if (!product) {
        logError("Product not found while adding subjects");
        return;
      }
      if (subjects.length === 0) {
        return;
      }

      const finalSubjects = product.subjects?.final || [];
      const newSubjects = subjects.filter(
        (subject) =>
          !finalSubjects.some((existing) => subjectMatches(subject, existing)),
      );

      if (newSubjects.length > 0) {
        const updatedSubjects = [...finalSubjects, ...newSubjects];

        await saveFinalSubjects(product.id, updatedSubjects);
      }
    },
    [product, saveFinalSubjects],
  );

  const handleChangeManualSubject = useCallback(
    async (item: BaseItem, value: string) => {
      if (!product) {
        logError("Product not found while changing manual subject");
        return;
      }
      const allFinalItems = product.subjects?.final || [];
      let newId = item.id;
      const subjectToChangeIndex = allFinalItems.findIndex(
        (finalItem) => finalItem.id === item.id,
      );

      const subjectToChange = allFinalItems[subjectToChangeIndex];

      if (subjectToChange?.source !== SubjectSource.MANUAL) {
        newId = v4();
      }

      const newFinal: FirestoreProductSubject[] = allFinalItems.map(
        (finalItem, index) => {
          if (index !== subjectToChangeIndex) {
            return finalItem;
          }

          const editedSubject: FirestoreProductSubject = {
            id: newId,
            responseId: finalItem.responseId ?? null,
            source: SubjectSource.MANUAL,
            subjectCode: finalItem.subjectCode ?? null,
            subjectHeadingText: value,
            reasoning: finalItem.reasoning ?? null,
            subjectSchemeIdentifier: finalItem.subjectSchemeIdentifier ?? null,
            ranking: null,
            rankingReasoning: null,
            keywordSearchQuery: finalItem.keywordSearchQuery ?? null,
            databaseIndex: finalItem.databaseIndex ?? null,
            hotnessScore: finalItem.hotnessScore ?? null,
          };

          return editedSubject;
        },
      );

      await saveFinalKeywords(product?.id, newFinal);
    },
    [product, saveFinalKeywords],
  );

  const handleChangeOrder = useCallback(
    async (
      items: BaseItem[],
      productId: string,
      type: "subject" | "qualifier",
    ) => {
      if (!product) {
        logError("Product not found while changing order");
        return;
      }

      const allFinalItems = product.subjects?.final || [];
      const subjects = filterSubjects(allFinalItems);
      const qualifiers = filterQualifiers(allFinalItems);
      const { final: keywords } = getKeywordsByType(product);

      const reorderItems = (source: FirestoreProductSubject[]) =>
        items
          .map((item) => source.find((s) => s.id === item.id))
          .filter(
            (item): item is FirestoreProductSubject => item !== undefined,
          );

      const sourceItems = type === "subject" ? subjects : qualifiers;
      const reorderedItems = reorderItems(sourceItems);

      if (
        reorderedItems.length !== sourceItems.length ||
        items.length !== sourceItems.length
      ) {
        logError("Subject reorder mismatch: some items could not be resolved");
        return;
      }

      const reorderedByType = {
        subject: [...reorderedItems, ...qualifiers, ...keywords],
        qualifier: [...subjects, ...reorderedItems, ...keywords],
      };

      const newOrder = reorderedByType[type];

      if (newOrder.length === 0) {
        return;
      }

      await saveFinalSubjects(productId, newOrder);
    },
    [product, saveFinalSubjects],
  );

  const handleSaveFinalKeywordItems = useCallback(
    async (items: BaseItem[]) => {
      if (!product) {
        throw new Error("Product not found while saving final keywords");
      }

      const allFinalItems = product.subjects?.final || [];
      const subjects = filterSubjects(allFinalItems);
      const qualifiers = filterQualifiers(allFinalItems);
      const { final: keywords } = getKeywordsByType(product);
      const changedKeywords = items
        .map((item) => keywords.find((keyword) => keyword.id === item.id))
        .filter((item): item is FirestoreProductSubject => item !== undefined);

      if (changedKeywords.length !== items.length) {
        throw new Error(
          "Final keyword list mismatch: some items could not be resolved",
        );
      }

      await saveFinalKeywords(product.id, [
        ...subjects,
        ...qualifiers,
        ...changedKeywords,
      ]);
    },
    [product, saveFinalKeywords],
  );

  const handleChangeWordOrder = useCallback(
    async (items: BaseItem[]) => {
      if (!product) {
        logError("Product not found while changing word order");
        return;
      }

      const currentWords = getKeywordsByType(product).finalWords;
      const reorderedWords = items
        .map((item) => currentWords.find((w) => w.id === item.id))
        .filter((item): item is FirestoreProductSubject => item !== undefined);

      if (reorderedWords.length !== currentWords.length) {
        logError("Word reorder mismatch: some words could not be resolved");
        return;
      }

      await saveFinalWordSubjects(product.id, reorderedWords);
    },
    [product, saveFinalWordSubjects],
  );

  return {
    handleToggleSubject,
    handleAddSubjects,
    handleChangeOrder,
    handleSaveFinalKeywordItems,
    handleChangeManualSubject,
    handleChangeWordOrder,
  };
};
